/**
 * Capacity Data API Hook
 * Provides data fetching for capacity analysis endpoints
 */

import { useQuery } from '@tanstack/react-query';
import api from '../api/client';

const API_BASE = '/planning';

// Types for capacity data
export interface CapacityDayData {
  date: string;
  available_hours: number;
  planned_hours: number;
  is_holiday: boolean;
  is_maintenance: boolean;
}

export interface WorkCenterCapacity {
  work_center_code: string;
  work_center_name: string;
  capacity_hours_per_day: number;
  days: CapacityDayData[];
}

export interface HeatmapDateData {
  planned_hours: number;
  capacity_hours: number;
  utilization_percent: number;
}

export interface HeatmapWorkCenter {
  work_center_code: string;
  work_center_name: string;
  dates: Record<string, HeatmapDateData>;
}

export interface HeatmapResponse {
  scenario_id: string;
  scenario_name: string;
  heatmap: HeatmapWorkCenter[];
}

export interface UtilizationWorkCenter {
  work_center_code: string;
  work_center_name: string;
  operation_count: number;
  total_planned_hours: number;
  total_capacity_hours: number;
  utilization_percent: number;
}

export interface UtilizationResponse {
  scenario_id: string;
  planning_days: number;
  work_centers: UtilizationWorkCenter[];
}

// Fetch capacity data for date range
async function fetchCapacityData(
  startDate: string,
  endDate: string,
  scenarioId?: string
): Promise<WorkCenterCapacity[]> {
  const params = new URLSearchParams({ start: startDate, end: endDate });
  if (scenarioId) params.append('scenario_id', scenarioId);

  const response = await api.get(`${API_BASE}/capacity/?${params}`);
  return response.data;
}

// Fetch heatmap data for a scenario
async function fetchHeatmapData(scenarioId: string): Promise<HeatmapResponse> {
  const response = await api.get(`${API_BASE}/capacity/heatmap/?scenario_id=${scenarioId}`);
  return response.data;
}

// Fetch utilization summary for a scenario
async function fetchUtilizationData(scenarioId: string): Promise<UtilizationResponse> {
  const response = await api.get(`${API_BASE}/capacity/utilization/?scenario_id=${scenarioId}`);
  return response.data;
}

// Hook: Fetch capacity data
export function useCapacityData(
  startDate: string,
  endDate: string,
  scenarioId?: string,
  enabled = true
) {
  return useQuery({
    queryKey: ['capacity-data', startDate, endDate, scenarioId],
    queryFn: () => fetchCapacityData(startDate, endDate, scenarioId),
    enabled: enabled && !!startDate && !!endDate,
    refetchInterval: 60000, // Refresh every minute
  });
}

// Hook: Fetch heatmap data
export function useCapacityHeatmap(scenarioId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['capacity-heatmap', scenarioId],
    queryFn: () => fetchHeatmapData(scenarioId!),
    enabled: enabled && !!scenarioId,
    refetchInterval: 60000,
  });
}

// Hook: Fetch utilization data
export function useCapacityUtilization(scenarioId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['capacity-utilization', scenarioId],
    queryFn: () => fetchUtilizationData(scenarioId!),
    enabled: enabled && !!scenarioId,
    refetchInterval: 60000,
  });
}

// Helper: Get utilization color based on percentage
export function getUtilizationColor(percent: number): {
  bg: string;
  text: string;
  intensity: string;
} {
  if (percent >= 100) {
    return { bg: 'bg-red-500', text: 'text-white', intensity: 'overloaded' };
  } else if (percent >= 90) {
    return { bg: 'bg-red-400', text: 'text-white', intensity: 'critical' };
  } else if (percent >= 75) {
    return { bg: 'bg-orange-400', text: 'text-white', intensity: 'high' };
  } else if (percent >= 50) {
    return { bg: 'bg-yellow-400', text: 'text-gray-900', intensity: 'medium' };
  } else if (percent >= 25) {
    return { bg: 'bg-green-300', text: 'text-gray-900', intensity: 'low' };
  } else if (percent > 0) {
    return { bg: 'bg-green-200', text: 'text-gray-900', intensity: 'minimal' };
  }
  return { bg: 'bg-gray-100', text: 'text-gray-500', intensity: 'idle' };
}

// Helper: Get utilization bar color (for progress bars)
export function getUtilizationBarColor(percent: number): string {
  if (percent >= 100) return 'bg-red-500';
  if (percent >= 90) return 'bg-red-400';
  if (percent >= 75) return 'bg-orange-400';
  if (percent >= 50) return 'bg-yellow-400';
  if (percent >= 25) return 'bg-green-400';
  return 'bg-green-300';
}

// Widget data helper
export function useCapacityWidgetData(scenarioId: string | undefined) {
  const heatmapQuery = useCapacityHeatmap(scenarioId);
  const utilizationQuery = useCapacityUtilization(scenarioId);

  return {
    heatmap: heatmapQuery.data,
    utilization: utilizationQuery.data,
    loading: heatmapQuery.isLoading || utilizationQuery.isLoading,
    error: heatmapQuery.error || utilizationQuery.error,
    refetch: () => {
      heatmapQuery.refetch();
      utilizationQuery.refetch();
    },
  };
}
